import * as Dfns from 'date-fns/fp';

import {
  FlightData,
  NetworkData,
  SubFlightData,
  UnmatchedDateRow,
  UnmatchedGroup
} from './use-streaming-wizard';

const PRETTY_DATE_FORMAT = 'M/d/yyyy';
export const MAX_COUNT = 100;

export const toPrettyDate = (date: string): string =>
  Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(date));

export const toPrettyNumber = (num: number | undefined): string =>
  Number(num ?? 0).toLocaleString();

export const byDateDescending = (a: UnmatchedDateRow, b: UnmatchedDateRow): number =>
  b.date.localeCompare(a.date);

export const toPrettyCPM = (num: number): string =>
  num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  });

interface FlightAdviceArgs {
  unmatchedInfo: UnmatchedGroup;
  sortedImpRows: UnmatchedDateRow[];
  networkData: NetworkData;
  hasSubs: boolean;
  derivedNetwork: string;
}

const adviseFromOverlappingFlight = (flight: FlightData, firstImpDate: string): string => {
  let bestSub: SubFlightData | undefined;
  for (const subFlight of flight.subFlights ?? []) {
    if (subFlight.start && subFlight.start > firstImpDate) {
      continue;
    }
    const effectiveEnd = subFlight.extended || subFlight.end;
    if (effectiveEnd && effectiveEnd < firstImpDate) {
      const bestEffectiveEnd = bestSub ? bestSub.extended || bestSub.end : undefined;
      if (!bestSub || (bestEffectiveEnd && bestEffectiveEnd < effectiveEnd)) {
        bestSub = subFlight;
      }
    }
  }

  if (bestSub) {
    return `Some of these impressions overlap with flight #${bestSub.flight_id}, which had this property on it before. I think you should extend sub-flight #${bestSub.id}.`;
  }
  return `Some of these impressions overlap with flight #${flight.id}, but it doesn't have a good sub flight to extend. I think you should use the "add" feature on it.`;
};

const adviseFromFlights = ({
  flights,
  firstImpDate,
  hasSubs,
  derivedNetwork
}: {
  flights: FlightData[];
  firstImpDate: string;
  hasSubs: boolean;
  derivedNetwork: string;
}): string => {
  if (!flights.length) {
    return `You have NO flights for ${derivedNetwork}. That shouldn't happen. Either accept these as bonus or make a fake flight for them in the buying platform.`;
  }

  let lastFlight: FlightData | undefined;
  for (const flight of flights) {
    // flight ends before first imp, then you save in last flight and move on
    if (flight.end < firstImpDate) {
      lastFlight = flight;
    } else if (firstImpDate < flight.start) {
      // If the flight is after the imp, break and we'll use whatever "lastFlight" is
      break;
    } else {
      // If the imp isn't after the flight or before the flight, it has to be DURING the flight.
      // If an impression overlaps with a non-DGO flight, then it's literally matched to that
      // flight. The only time that isn't true is in the DGO case.
      if (!hasSubs) {
        return "Uh oh. It looks like you have impressions that overlap with a flight and don't match, but this isn't a network with a parent. This should not be possible. Tell someone from engineering!";
      }
      return adviseFromOverlappingFlight(flight, firstImpDate);
    }
  }

  if (!lastFlight) {
    return 'Your impressions start before your first flight. They are probably test impressions that you should just accept.';
  }
  return `I think you should extend flight #${lastFlight.id}.`;
};

// Figures out what the wizard should suggest doing with a network's unmatched impressions: accept
// them as bonus, extend an existing flight, or add a sub-flight. Mirrors the flight/sub-flight
// matching rules the buying platform uses, so the suggestion lines up with what's actually
// eligible to extend.
export const buildFlightAdvice = ({
  unmatchedInfo,
  sortedImpRows,
  networkData,
  hasSubs,
  derivedNetwork
}: FlightAdviceArgs): string => {
  let message: string;
  if (unmatchedInfo.count < MAX_COUNT) {
    message = `You have fewer than ${MAX_COUNT} impressions here. You probably want to just mark them as bonus.`;
  } else {
    // Everything will be based on the first imp date we have. We want flights in ascending
    // chronological order.
    const firstImpDate = sortedImpRows[sortedImpRows.length - 1]?.date;
    const flights = [...networkData.flights].reverse();
    message = adviseFromFlights({ flights, firstImpDate, hasSubs, derivedNetwork });
  }

  if (networkData.platform.includes('RTB')) {
    message = `${message}\nBy the way, this is RTB. Accepting impressions doesn't make them "bonus", just hidden.`;
  }

  return message;
};
